import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import {
  FiX, FiSettings, FiImage, FiLock, FiFolder,
  FiChevronRight, FiSearch, FiArrowLeft, FiAlertCircle,
  FiCheckCircle, FiTrash2, FiUpload, FiShare2, FiGlobe,
  FiCopy, FiCheck, FiExternalLink, FiLoader, FiRefreshCw, FiStar,
  FiMessageCircle
} from 'react-icons/fi';
import supabase from '../../supabase/supabase';
import { addNetlifyDomainAlias, removeNetlifyDomainAlias, setNetlifyPrimaryDomain } from '../../utils/netlify';

const NETLIFY_NS = [
  'dns1.p09.nsone.net',
  'dns2.p09.nsone.net',
  'dns3.p09.nsone.net',
  'dns4.p09.nsone.net',
];

async function checkDnsStatus(domain) {
  try {
    const rootDomain = domain.replace(/^www\./, '');
    const res = await fetch(
      `https://cloudflare-dns.com/dns-query?name=${rootDomain}&type=NS`,
      { headers: { Accept: 'application/dns-json' } }
    );
    if (!res.ok) return 'unknown';
    const data = await res.json();
    const answers = data.Answer || [];
    if (answers.length === 0) return 'pending';
    const nsRecords = answers.map(a => a.data?.toLowerCase().replace(/\.$/, ''));
    const matchCount = NETLIFY_NS.filter(ns => nsRecords.some(r => r.includes(ns))).length;
    if (matchCount >= 2) return 'active';
    if (matchCount >= 1) return 'syncing';
    return 'pending';
  } catch {
    return 'unknown';
  }
}

function DnsStatusBadge({ domain }) {
  const [status, setStatus] = useState('checking');
  const [lastChecked, setLastChecked] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const runCheck = useCallback(async () => {
    if (!domain) return;
    setIsRefreshing(true);
    setStatus('checking');
    const result = await checkDnsStatus(domain);
    setStatus(result);
    setLastChecked(new Date());
    setIsRefreshing(false);
  }, [domain]);

  useEffect(() => { runCheck(); }, [runCheck]);

  const config = {
    checking: { dot: 'bg-gray-400 animate-pulse',    label: 'Checking DNS…',       text: 'text-gray-400',   border: 'border-gray-500/20',   bg: 'bg-gray-500/10' },
    active:   { dot: 'bg-green-400',                 label: 'DNS Active',           text: 'text-green-400',  border: 'border-green-500/30',  bg: 'bg-green-500/10' },
    syncing:  { dot: 'bg-yellow-400 animate-pulse',  label: 'DNS Syncing',          text: 'text-yellow-400', border: 'border-yellow-500/30', bg: 'bg-yellow-500/10' },
    pending:  { dot: 'bg-orange-400 animate-pulse',  label: 'Awaiting NS Update',   text: 'text-orange-400', border: 'border-orange-500/30', bg: 'bg-orange-500/10' },
    unknown:  { dot: 'bg-red-400',                   label: 'DNS Check Failed',     text: 'text-red-400',    border: 'border-red-500/30',    bg: 'bg-red-500/10' },
  };

  const statusMessages = {
    active:   'Your nameservers are pointing to Netlify DNS. Your custom domain is live and set as primary!',
    syncing:  'Some nameservers are updated. DNS is still propagating — check back in a few hours.',
    pending:  'Nameservers have not been updated yet. Follow the instructions below to point them to Netlify.',
    unknown:  'Could not verify DNS status. This may be a temporary issue — try refreshing.',
    checking: 'Running DNS lookup…',
  };

  const c = config[status] || config.unknown;

  return (
    <div className={`rounded-xl border p-4 space-y-3 ${c.bg} ${c.border}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2.5">
          <span className="relative flex h-2.5 w-2.5">
            {(status === 'active' || status === 'syncing' || status === 'pending') && (
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${c.dot.replace(' animate-pulse', '')}`} />
            )}
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${c.dot}`} />
          </span>
          <span className={`text-xs font-black uppercase tracking-widest ${c.text}`}>{c.label}</span>
        </div>
        <button
          onClick={runCheck}
          disabled={isRefreshing}
          title="Refresh DNS status"
          className="p-1.5 text-gray-500 hover:text-gray-300 hover:bg-[#2c313a] rounded-lg transition-all disabled:opacity-50"
        >
          <SafeIcon icon={FiRefreshCw} className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <p className="text-xs text-gray-400 leading-relaxed">{statusMessages[status]}</p>
      {lastChecked && (
        <p className="text-[10px] text-gray-600">
          Last checked: {lastChecked.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </p>
      )}
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handle = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <button onClick={handle} className="p-1.5 text-gray-500 hover:text-[#c8f542] transition-colors rounded-lg hover:bg-[#c8f542]/10">
      <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-3.5 h-3.5" />
    </button>
  );
}

function NsRow({ value }) {
  return (
    <div className="flex items-center justify-between bg-[#111318] border border-[#2c313a] rounded-xl px-4 py-3">
      <span className="text-sm font-mono text-[#c8f542]">{value}</span>
      <CopyButton text={value} />
    </div>
  );
}

function DomainsView({ onBack, onClose, activeWebsiteId }) {
  const [siteData, setSiteData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [customDomain, setCustomDomain] = useState('');
  const [inputDomain, setInputDomain] = useState('');
  const [netlifyStep, setNetlifyStep] = useState('idle');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isRemoving, setIsRemoving] = useState(false);
  const [showNsInstructions, setShowNsInstructions] = useState(false);

  useEffect(() => {
    if (!activeWebsiteId) return;
    const load = async () => {
      setIsLoading(true);
      try {
        const { data, error: fetchErr } = await supabase
          .from('websites_1772750000000')
          .select('name, netlify_site_id, netlify_subdomain, custom_domain')
          .eq('id', activeWebsiteId)
          .single();
        if (fetchErr) throw fetchErr;
        setSiteData(data);
        setCustomDomain(data?.custom_domain || '');
        if (data?.custom_domain) setShowNsInstructions(true);
      } catch {
        setError('Failed to load domain settings.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [activeWebsiteId]);

  const isValidDomain = (d) =>
    /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(
      d.replace(/^https?:\/\//, '').replace(/\/$/, '')
    );

  const stripProtocol = (d) =>
    d.replace(/^https?:\/\//, '').replace(/\/$/, '').toLowerCase().trim();

  const handleConnect = async () => {
    const cleaned = stripProtocol(inputDomain);
    if (!isValidDomain(cleaned)) {
      setError('Please enter a valid domain (e.g. www.yourdomain.com)');
      return;
    }
    if (!siteData?.netlify_site_id) {
      setError('This site has not been published to Netlify yet. Please publish your site first, then connect a domain.');
      return;
    }

    setError('');
    setSuccess('');

    try {
      setNetlifyStep('alias');
      await addNetlifyDomainAlias(siteData.netlify_site_id, cleaned);

      setNetlifyStep('primary');
      await setNetlifyPrimaryDomain(siteData.netlify_site_id, cleaned);

      const { error: dbErr } = await supabase
        .from('websites_1772750000000')
        .update({ custom_domain: cleaned, updated_at: new Date().toISOString() })
        .eq('id', activeWebsiteId);
      if (dbErr) throw dbErr;

      setCustomDomain(cleaned);
      setInputDomain('');
      setNetlifyStep('done');
      setShowNsInstructions(true);
      setSuccess(`"${cleaned}" connected and set as primary domain! Update your nameservers below to go live.`);
      setTimeout(() => setSuccess(''), 8000);
    } catch (err) {
      setNetlifyStep('error');
      setError(err.message || 'Failed to connect domain.');
    }
  };

  const handleRemove = async () => {
    if (!customDomain) return;
    setIsRemoving(true);
    setError('');
    try {
      if (siteData?.netlify_site_id) {
        // Pass the SideHustlr subdomain as the fallback primary domain
        // so Netlify resets to it instead of trying to use the .netlify.app default
        await removeNetlifyDomainAlias(
          siteData.netlify_site_id,
          customDomain,
          siteData.netlify_subdomain || null
        );
      }

      const { error: dbErr } = await supabase
        .from('websites_1772750000000')
        .update({ custom_domain: null, updated_at: new Date().toISOString() })
        .eq('id', activeWebsiteId);
      if (dbErr) throw dbErr;

      setCustomDomain('');
      setShowNsInstructions(false);
      setNetlifyStep('idle');

      const subdomain = siteData?.netlify_subdomain;
      setSuccess(
        subdomain
          ? `Custom domain removed. Your site is back on ${subdomain}`
          : 'Custom domain removed successfully.'
      );
      setTimeout(() => setSuccess(''), 6000);
    } catch (err) {
      setError(err.message || 'Failed to remove domain.');
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-5 flex items-center bg-[#111318] border-b border-[#2c313a] shrink-0">
        <button onClick={onBack} className="p-2 -ml-1 mr-3 text-gray-500 hover:bg-[#2c313a] rounded-full transition-colors">
          <SafeIcon icon={FiArrowLeft} className="w-5 h-5 text-gray-300" />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-black text-white tracking-tight">Domain Management</h2>
          <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold mt-0.5">
            Manage your subdomain and custom domain
          </p>
        </div>
        <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-300 hover:bg-[#2c313a] rounded-xl transition-colors">
          <SafeIcon icon={FiX} className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-5">
        {error && (
          <div className="flex items-start space-x-2 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm">
            <SafeIcon icon={FiAlertCircle} className="w-4 h-4 shrink-0 mt-0.5" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError('')}><SafeIcon icon={FiX} className="w-3 h-3" /></button>
          </div>
        )}
        {success && (
          <div className="flex items-center space-x-2 bg-green-500/10 border border-green-500/30 rounded-xl px-4 py-3 text-green-400 text-sm">
            <SafeIcon icon={FiCheckCircle} className="w-4 h-4 shrink-0" />
            <span>{success}</span>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-3 border-[#c8f542]/30 border-t-[#c8f542] rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Subdomain section */}
            <div className="bg-[#1e2228] border border-[#2c313a] rounded-2xl p-5 space-y-3">
              <div className="flex items-center space-x-2 mb-1">
                <SafeIcon icon={FiGlobe} className="w-4 h-4 text-[#c8f542]" />
                <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest">Your SideHustlr Subdomain</p>
              </div>
              {siteData?.netlify_subdomain ? (
                <div className="flex items-center justify-between bg-[#111318] border border-[#2c313a] rounded-xl px-4 py-3">
                  <div>
                    <p className="text-sm font-bold text-[#c8f542] font-mono">{siteData.netlify_subdomain}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      {customDomain
                        ? 'Subdomain — currently overridden by your custom domain'
                        : 'Your free SideHustlr subdomain (active primary)'}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CopyButton text={`https://${siteData.netlify_subdomain}`} />
                    <a href={`https://${siteData.netlify_subdomain}`} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 text-gray-500 hover:text-[#c8f542] transition-colors rounded-lg hover:bg-[#c8f542]/10">
                      <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              ) : (
                <div className="bg-[#111318] border border-[#2c313a] rounded-xl px-4 py-3">
                  <p className="text-sm text-gray-500">No subdomain yet — publish your site first to get one.</p>
                </div>
              )}
            </div>

            {/* Custom domain section */}
            <div className="bg-[#1e2228] border border-[#2c313a] rounded-2xl p-5 space-y-4">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiStar} className="w-4 h-4 text-[#c8f542]" />
                <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest">Connect Custom Domain</p>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                Connect your own domain (e.g.{' '}
                <span className="text-gray-300 font-mono">www.yourbusiness.com</span>). It will be added as an alias <em>and</em> set as the <strong className="text-white">primary domain</strong> on Netlify.
              </p>

              {customDomain ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-[#111318] border border-green-500/30 rounded-xl px-4 py-3">
                    <div>
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiStar} className="w-3.5 h-3.5 text-[#c8f542]" />
                        <p className="text-sm font-bold text-white font-mono">{customDomain}</p>
                        <span className="text-[9px] font-black bg-[#c8f542]/15 text-[#c8f542] px-2 py-0.5 rounded-full uppercase tracking-wider">Primary</span>
                      </div>
                      <p className="text-[10px] text-green-400 mt-0.5">Alias added · Set as primary domain on Netlify</p>
                    </div>
                    <div className="flex items-center space-x-1">
                      <CopyButton text={`https://${customDomain}`} />
                      <a href={`https://${customDomain}`} target="_blank" rel="noopener noreferrer"
                        className="p-1.5 text-gray-500 hover:text-[#c8f542] transition-colors rounded-lg hover:bg-[#c8f542]/10">
                        <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
                      </a>
                      <button
                        onClick={handleRemove}
                        disabled={isRemoving}
                        className="flex items-center space-x-1 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold rounded-lg border border-red-500/20 transition-all ml-1"
                      >
                        {isRemoving
                          ? <SafeIcon icon={FiLoader} className="w-3 h-3 animate-spin" />
                          : <SafeIcon icon={FiTrash2} className="w-3 h-3" />}
                        <span>{isRemoving ? 'Removing…' : 'Remove'}</span>
                      </button>
                    </div>
                  </div>

                  <DnsStatusBadge domain={customDomain} />

                  <button
                    onClick={() => setShowNsInstructions(p => !p)}
                    className="w-full text-left text-xs text-[#c8f542] font-bold flex items-center space-x-1 hover:underline"
                  >
                    <SafeIcon icon={FiChevronRight} className={`w-3.5 h-3.5 transition-transform ${showNsInstructions ? 'rotate-90' : ''}`} />
                    <span>{showNsInstructions ? 'Hide' : 'Show'} nameserver instructions</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={inputDomain}
                      onChange={(e) => { setInputDomain(e.target.value); setError(''); }}
                      placeholder="www.yourdomain.com"
                      disabled={netlifyStep === 'alias' || netlifyStep === 'primary'}
                      className="flex-1 px-4 py-3 bg-[#111318] border border-[#2c313a] rounded-xl text-sm text-white placeholder-gray-600 outline-none focus:border-[#c8f542]/50 transition-all font-mono disabled:opacity-50"
                      onKeyDown={(e) => e.key === 'Enter' && inputDomain && handleConnect()}
                    />
                    <button
                      onClick={handleConnect}
                      disabled={!inputDomain.trim() || netlifyStep === 'alias' || netlifyStep === 'primary'}
                      className="px-5 py-3 bg-[#c8f542] text-black font-black text-xs uppercase tracking-widest rounded-xl hover:bg-[#d4ff50] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shrink-0"
                    >
                      {(netlifyStep === 'alias' || netlifyStep === 'primary')
                        ? <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                        : <span>Connect</span>}
                    </button>
                  </div>

                  {(netlifyStep === 'alias' || netlifyStep === 'primary') && (
                    <div className="space-y-2">
                      <div className={`flex items-center space-x-2 text-xs ${netlifyStep === 'alias' ? 'text-[#c8f542]' : 'text-green-400'}`}>
                        {netlifyStep === 'alias'
                          ? <SafeIcon icon={FiLoader} className="w-3.5 h-3.5 animate-spin" />
                          : <SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5" />}
                        <span>Step 1: Adding domain alias to Netlify…</span>
                      </div>
                      <div className={`flex items-center space-x-2 text-xs ${netlifyStep === 'primary' ? 'text-[#c8f542]' : 'text-gray-600'}`}>
                        {netlifyStep === 'primary'
                          ? <SafeIcon icon={FiLoader} className="w-3.5 h-3.5 animate-spin" />
                          : <SafeIcon icon={FiStar} className="w-3.5 h-3.5" />}
                        <span>Step 2: Setting as primary domain…</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <AnimatePresence>
              {showNsInstructions && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="bg-[#1e2228] border border-[#c8f542]/20 rounded-2xl p-5 space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-[#c8f542]/10 rounded-xl flex items-center justify-center shrink-0 mt-0.5">
                        <SafeIcon icon={FiGlobe} className="w-4 h-4 text-[#c8f542]" />
                      </div>
                      <div>
                        <p className="text-sm font-black text-white">Point your nameservers to Netlify DNS</p>
                        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                          Log in to your domain registrar (GoDaddy, Namecheap, etc.) and replace your current nameservers with these four:
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {NETLIFY_NS.map((ns) => <NsRow key={ns} value={ns} />)}
                    </div>
                    <div className="bg-[#111318] border border-[#2c313a] rounded-xl p-4 space-y-2">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">What happens next?</p>
                      <ol className="space-y-1.5 text-xs text-gray-400 list-decimal list-inside leading-relaxed">
                        <li>Replace your registrar's nameservers with the four above</li>
                        <li>DNS propagation takes <span className="text-white font-bold">24–48 hours</span></li>
                        <li>The status indicator above will turn <span className="text-green-400 font-bold">green</span> once active</li>
                        <li>Your custom domain is already the <span className="text-[#c8f542] font-bold">primary domain</span> on Netlify — SSL is auto-provisioned once DNS resolves</li>
                      </ol>
                    </div>
                    {siteData?.netlify_subdomain && customDomain && (
                      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl px-4 py-3">
                        <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1">Configuration Summary</p>
                        <p className="text-xs text-blue-300 leading-relaxed">
                          <span className="font-mono font-bold text-white">{customDomain}</span> is the <span className="text-[#c8f542] font-bold">primary domain</span>. Once DNS propagates, Netlify will redirect your subdomain{' '}
                          <span className="font-mono">{siteData.netlify_subdomain}</span> to your custom domain.
                        </p>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>

      <div className="p-6 bg-[#0a0a0a] border-t border-[#2c313a] shrink-0">
        <button onClick={onBack} className="w-full py-4 font-black text-gray-400 hover:text-gray-300 uppercase tracking-widest text-xs transition-colors">
          Back to Settings
        </button>
      </div>
    </div>
  );
}

function SeoView({ onBack, onClose, activeWebsiteId }) {
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!activeWebsiteId) return;
    const loadSeo = async () => {
      setIsLoading(true);
      try {
        const { data, error: fetchError } = await supabase
          .from('websites_1772750000000')
          .select('seo_title, seo_description')
          .eq('id', activeWebsiteId)
          .single();
        if (fetchError) throw fetchError;
        if (data) { setSeoTitle(data.seo_title || ''); setSeoDescription(data.seo_description || ''); }
      } catch (err) {
        console.error('Failed to load SEO settings:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadSeo();
  }, [activeWebsiteId]);

  const handleSave = async () => {
    if (!activeWebsiteId) return;
    setIsSaving(true); setError('');
    try {
      const { error: updateError } = await supabase
        .from('websites_1772750000000')
        .update({ seo_title: seoTitle, seo_description: seoDescription, updated_at: new Date().toISOString() })
        .eq('id', activeWebsiteId);
      if (updateError) throw updateError;
      setSuccess('SEO settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-5 flex items-center bg-[#111318] border-b border-[#2c313a] shrink-0">
        <button onClick={onBack} className="p-2 -ml-1 mr-3 text-gray-500 hover:bg-[#2c313a] rounded-full transition-colors">
          <SafeIcon icon={FiArrowLeft} className="w-5 h-5 text-gray-300" />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-black text-white tracking-tight">SEO Settings</h2>
          <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold mt-0.5">Configure meta tags and search engine optimization</p>
        </div>
        <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-300 hover:bg-[#2c313a] rounded-xl transition-colors">
          <SafeIcon icon={FiX} className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
        {error && (
          <div className="flex items-center space-x-2 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm">
            <SafeIcon icon={FiAlertCircle} className="w-4 h-4 shrink-0" /><span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto"><SafeIcon icon={FiX} className="w-3 h-3" /></button>
          </div>
        )}
        {success && (
          <div className="flex items-center space-x-2 bg-green-500/10 border border-green-500/30 rounded-xl px-4 py-3 text-green-400 text-sm">
            <SafeIcon icon={FiCheckCircle} className="w-4 h-4 shrink-0" /><span>{success}</span>
          </div>
        )}
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-8 h-8 border-3 border-[#c8f542]/30 border-t-[#c8f542] rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="bg-[#1e2228] border border-[#2c313a] rounded-2xl p-5 space-y-4">
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center mb-2">Page Title</label>
                <input type="text" value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} placeholder="e.g. My Website | Best Products Online"
                  className="w-full px-4 py-3 bg-[#111318] border border-[#2c313a] rounded-xl text-sm text-white placeholder-gray-600 outline-none focus:border-[#c8f542]/50 transition-all" maxLength={60} />
                <div className="flex justify-between mt-2 text-[10px] text-gray-500">
                  <span>Recommended: 10-60 characters</span>
                  <span className={seoTitle.length > 60 ? 'text-red-400' : ''}>{seoTitle.length}/60</span>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center mb-2">Meta Description</label>
                <textarea value={seoDescription} onChange={(e) => setSeoDescription(e.target.value)}
                  placeholder="A brief description of your page that appears in search results." rows={4}
                  className="w-full px-4 py-3 bg-[#111318] border border-[#2c313a] rounded-xl text-sm text-white placeholder-gray-600 outline-none focus:border-[#c8f542]/50 transition-all resize-none" maxLength={160} />
                <div className="flex justify-between mt-2 text-[10px] text-gray-500">
                  <span>Recommended: 50-160 characters</span>
                  <span className={seoDescription.length > 160 ? 'text-red-400' : ''}>{seoDescription.length}/160</span>
                </div>
              </div>
            </div>
            <div className="bg-[#1e2228] border border-[#2c313a] rounded-2xl p-5 space-y-4">
              <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Google Search Preview</p>
              <div className="bg-white p-4 rounded-xl">
                <span className="text-[#1a0dab] text-xl cursor-pointer hover:underline block truncate">{seoTitle || 'Your Page Title Here'}</span>
                <span className="text-[#006621] text-[13px] block mt-1">https://yourdomain.com/</span>
                <span className="text-[#545454] text-sm block mt-1 leading-snug">{seoDescription || 'Your meta description will appear here.'}</span>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="p-6 bg-[#0a0a0a] border-t border-[#2c313a] flex space-x-4 shrink-0">
        <button onClick={onBack} className="flex-1 py-4 font-black text-gray-400 hover:text-gray-300 uppercase tracking-widest text-xs transition-colors">Cancel</button>
        <button onClick={handleSave} disabled={isSaving}
          className="flex-[2] py-4 bg-[#c8f542] text-black font-black uppercase tracking-widest text-xs rounded-2xl shadow-xl hover:bg-[#d4ff50] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2">
          {isSaving ? (<><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /><span>Saving…</span></>) : (<span>Save Settings</span>)}
        </button>
      </div>
    </div>
  );
}

function GeneralView({ onBack, onClose, activeWebsiteId }) {
  const [faviconUrl, setFaviconUrl] = useState('');
  const [shareImageUrl, setShareImageUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!activeWebsiteId) return;
    const load = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('websites_1772750000000')
          .select('favicon_url, share_image_url')
          .eq('id', activeWebsiteId)
          .single();
        if (fetchError) { console.error('Failed to load general settings:', fetchError); return; }
        if (data) { setFaviconUrl(data.favicon_url || ''); setShareImageUrl(data.share_image_url || ''); }
      } catch (err) { console.error('General settings load error:', err); }
    };
    load();
  }, [activeWebsiteId]);

  const handleImageUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please upload an image file'); return; }
    setIsUploading(true); setUploadingType(type); setError('');
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${type}-${activeWebsiteId}-${Date.now()}.${fileExt}`;
      const filePath = `${activeWebsiteId}/${fileName}`;
      const { error: uploadError } = await supabase.storage.from('media').upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('media').getPublicUrl(filePath);
      if (type === 'favicon') setFaviconUrl(publicUrl);
      else setShareImageUrl(publicUrl);
      setSuccess(`${type === 'favicon' ? 'Favicon' : 'Share image'} uploaded successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.message || 'Failed to upload image'); }
    finally { setIsUploading(false); setUploadingType(null); }
  };

  const handleSave = async () => {
    if (!activeWebsiteId) return;
    setIsSaving(true); setError('');
    try {
      const { error: updateError } = await supabase
        .from('websites_1772750000000')
        .update({ favicon_url: faviconUrl || null, share_image_url: shareImageUrl || null, updated_at: new Date().toISOString() })
        .eq('id', activeWebsiteId);
      if (updateError) throw updateError;
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.message || 'Failed to save settings'); }
    finally { setIsSaving(false); }
  };

  const handleDelete = async (type) => {
    if (!activeWebsiteId) return;
    try {
      const updateData = type === 'favicon' ? { favicon_url: null } : { share_image_url: null };
      const { error: updateError } = await supabase
        .from('websites_1772750000000')
        .update({ ...updateData, updated_at: new Date().toISOString() })
        .eq('id', activeWebsiteId);
      if (updateError) throw updateError;
      if (type === 'favicon') setFaviconUrl('');
      else setShareImageUrl('');
      setSuccess(`${type === 'favicon' ? 'Favicon' : 'Share image'} removed`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.message || 'Failed to delete image'); }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-5 flex items-center bg-[#111318] border-b border-[#2c313a] shrink-0">
        <button onClick={onBack} className="p-2 -ml-1 mr-3 text-gray-500 hover:bg-[#2c313a] rounded-full transition-colors">
          <SafeIcon icon={FiArrowLeft} className="w-5 h-5 text-gray-300" />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-black text-white tracking-tight">General Settings</h2>
          <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold mt-0.5">Configure website identity and branding</p>
        </div>
        <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-300 hover:bg-[#2c313a] rounded-xl transition-colors">
          <SafeIcon icon={FiX} className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
        {error && (
          <div className="flex items-center space-x-2 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm">
            <SafeIcon icon={FiAlertCircle} className="w-4 h-4 shrink-0" /><span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto"><SafeIcon icon={FiX} className="w-3 h-3" /></button>
          </div>
        )}
        {success && (
          <div className="flex items-center space-x-2 bg-green-500/10 border border-green-500/30 rounded-xl px-4 py-3 text-green-400 text-sm">
            <SafeIcon icon={FiCheckCircle} className="w-4 h-4 shrink-0" /><span>{success}</span>
          </div>
        )}
        <div className="bg-[#1e2228] border border-[#2c313a] rounded-2xl p-5 space-y-4">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiImage} className="w-4 h-4 text-[#c8f542]" />
            <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest">Website Favicon</p>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">Upload a small icon (16x16 or 32x32 pixels) that appears in browser tabs and bookmarks.</p>
          {faviconUrl ? (
            <div className="bg-[#111318] rounded-xl p-4 border border-[#2c313a]">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold text-gray-400">Current Favicon</p>
                <button onClick={() => handleDelete('favicon')} className="flex items-center space-x-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold rounded-lg border border-red-500/20 transition-all">
                  <SafeIcon icon={FiTrash2} className="w-3 h-3" /><span>Remove</span>
                </button>
              </div>
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-white rounded-xl border-2 border-[#2c313a] flex items-center justify-center p-2">
                  <img src={faviconUrl} alt="Favicon" className="w-full h-full object-contain" />
                </div>
                <p className="text-xs text-gray-400 font-mono truncate flex-1">{faviconUrl}</p>
              </div>
            </div>
          ) : (
            <div className="relative">
              <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'favicon')} disabled={isUploading}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed" id="favicon-upload" />
              <label htmlFor="favicon-upload" className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 transition-all cursor-pointer ${isUploading && uploadingType === 'favicon' ? 'border-[#c8f542]/50 bg-[#c8f542]/5' : 'border-[#2c313a] hover:border-[#c8f542]/50 hover:bg-[#111318]'}`}>
                {isUploading && uploadingType === 'favicon'
                  ? (<><div className="w-8 h-8 border-3 border-[#c8f542]/30 border-t-[#c8f542] rounded-full animate-spin mb-3" /><p className="text-sm font-bold text-gray-400">Uploading…</p></>)
                  : (<><SafeIcon icon={FiUpload} className="w-8 h-8 text-gray-500 mb-3" /><p className="text-sm font-bold text-gray-300 mb-1">Click to upload favicon</p><p className="text-xs text-gray-500">PNG, ICO, or SVG (max 1MB)</p></>)}
              </label>
            </div>
          )}
        </div>
        <div className="bg-[#1e2228] border border-[#2c313a] rounded-2xl p-5 space-y-4">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiShare2} className="w-4 h-4 text-[#c8f542]" />
            <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest">Social Share Image</p>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">Upload an image that appears when your website is shared on social media. Recommended: 1200x630px.</p>
          {shareImageUrl ? (
            <div className="bg-[#111318] rounded-xl p-4 border border-[#2c313a]">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold text-gray-400">Current Share Image</p>
                <button onClick={() => handleDelete('share')} className="flex items-center space-x-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold rounded-lg border border-red-500/20 transition-all">
                  <SafeIcon icon={FiTrash2} className="w-3 h-3" /><span>Remove</span>
                </button>
              </div>
              <div className="aspect-[1.91/1] bg-[#0a0a0a] rounded-lg border border-[#2c313a] overflow-hidden">
                <img src={shareImageUrl} alt="Share preview" className="w-full h-full object-cover" />
              </div>
            </div>
          ) : (
            <div className="relative">
              <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'share')} disabled={isUploading}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed" id="share-upload" />
              <label htmlFor="share-upload" className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 transition-all cursor-pointer ${isUploading && uploadingType === 'share' ? 'border-[#c8f542]/50 bg-[#c8f542]/5' : 'border-[#2c313a] hover:border-[#c8f542]/50 hover:bg-[#111318]'}`}>
                {isUploading && uploadingType === 'share'
                  ? (<><div className="w-8 h-8 border-3 border-[#c8f542]/30 border-t-[#c8f542] rounded-full animate-spin mb-3" /><p className="text-sm font-bold text-gray-400">Uploading…</p></>)
                  : (<><SafeIcon icon={FiUpload} className="w-8 h-8 text-gray-500 mb-3" /><p className="text-sm font-bold text-gray-300 mb-1">Click to upload share image</p><p className="text-xs text-gray-500">JPG or PNG (1200x630px recommended)</p></>)}
              </label>
            </div>
          )}
        </div>
      </div>
      <div className="p-6 bg-[#0a0a0a] border-t border-[#2c313a] flex space-x-4 shrink-0">
        <button onClick={onBack} className="flex-1 py-4 font-black text-gray-400 hover:text-gray-300 uppercase tracking-widest text-xs transition-colors">Back</button>
        <button onClick={handleSave} disabled={isSaving}
          className="flex-[2] py-4 bg-[#c8f542] text-black font-black uppercase tracking-widest text-xs rounded-2xl shadow-xl hover:bg-[#d4ff50] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2">
          {isSaving ? (<><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /><span>Saving…</span></>) : (<span>Save Settings</span>)}
        </button>
      </div>
    </div>
  );
}

function WidgetsView({ onBack, onClose, activeWebsiteId }) {
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!activeWebsiteId) return;
    const loadWidgets = async () => {
      setIsLoading(true);
      try {
        const { data, error: fetchError } = await supabase
          .from('websites_1772750000000')
          .select('layout_data')
          .eq('id', activeWebsiteId)
          .single();
        if (fetchError) throw fetchError;
        if (data && data.layout_data?.whatsappWidget) {
          setWhatsappEnabled(data.layout_data.whatsappWidget.enabled || false);
          setWhatsappNumber(data.layout_data.whatsappWidget.number || '');
        }
      } catch (err) {
        console.error('Failed to load widget settings:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadWidgets();
  }, [activeWebsiteId]);

  const handleSave = async () => {
    if (!activeWebsiteId) return;
    setIsSaving(true); setError('');
    try {
      const { data: existingData } = await supabase
        .from('websites_1772750000000')
        .select('layout_data')
        .eq('id', activeWebsiteId)
        .single();
      const currentLayoutData = existingData?.layout_data || {};
      const { error: updateError } = await supabase
        .from('websites_1772750000000')
        .update({
          layout_data: { ...currentLayoutData, whatsappWidget: { enabled: whatsappEnabled, number: whatsappNumber } },
          updated_at: new Date().toISOString()
        })
        .eq('id', activeWebsiteId);
      if (updateError) throw updateError;
      setSuccess('Widget settings saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-5 flex items-center bg-[#111318] border-b border-[#2c313a] shrink-0">
        <button onClick={onBack} className="p-2 -ml-1 mr-3 text-gray-500 hover:bg-[#2c313a] rounded-full transition-colors">
          <SafeIcon icon={FiArrowLeft} className="w-5 h-5 text-gray-300" />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-black text-white tracking-tight">Widgets</h2>
          <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold mt-0.5">Floating buttons and third-party tools</p>
        </div>
        <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-300 hover:bg-[#2c313a] rounded-xl transition-colors">
          <SafeIcon icon={FiX} className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
        {error && (
          <div className="flex items-center space-x-2 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm">
            <SafeIcon icon={FiAlertCircle} className="w-4 h-4 shrink-0" /><span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto"><SafeIcon icon={FiX} className="w-3 h-3" /></button>
          </div>
        )}
        {success && (
          <div className="flex items-center space-x-2 bg-green-500/10 border border-green-500/30 rounded-xl px-4 py-3 text-green-400 text-sm">
            <SafeIcon icon={FiCheckCircle} className="w-4 h-4 shrink-0" /><span>{success}</span>
          </div>
        )}
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-8 h-8 border-3 border-[#c8f542]/30 border-t-[#c8f542] rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-[#1e2228] border border-[#2c313a] rounded-2xl p-5 space-y-5">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-500/10 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiMessageCircle} className="w-5 h-5 text-green-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-black text-white">WhatsApp Chat Button</h3>
                <p className="text-xs text-gray-500 mt-0.5">Add a floating WhatsApp button to your website</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={whatsappEnabled} onChange={(e) => setWhatsappEnabled(e.target.checked)} className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
              </label>
            </div>
            <AnimatePresence>
              {whatsappEnabled && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="space-y-3 pt-3 border-t border-[#2c313a]">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">WhatsApp Number</label>
                  <input type="tel" value={whatsappNumber} onChange={(e) => setWhatsappNumber(e.target.value)} placeholder="+27123456789"
                    className="w-full px-4 py-3 bg-[#111318] border border-[#2c313a] rounded-xl text-sm text-white placeholder-gray-600 outline-none focus:border-green-500/50 transition-all" />
                  <p className="text-xs text-gray-500 leading-relaxed">Enter your WhatsApp number with country code (e.g., +27 for South Africa).</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
      <div className="p-6 bg-[#0a0a0a] border-t border-[#2c313a] flex space-x-4 shrink-0">
        <button onClick={onBack} className="flex-1 py-4 font-black text-gray-400 hover:text-gray-300 uppercase tracking-widest text-xs transition-colors">Cancel</button>
        <button onClick={handleSave} disabled={isSaving}
          className="flex-[2] py-4 bg-[#c8f542] text-black font-black uppercase tracking-widest text-xs rounded-2xl shadow-xl hover:bg-[#d4ff50] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2">
          {isSaving ? (<><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /><span>Saving…</span></>) : (<span>Save Settings</span>)}
        </button>
      </div>
    </div>
  );
}

const SettingsPopup = ({ onClose, onOpenLibrary, activeWebsiteName, activeWebsiteId, onOpenPreview }) => {
  const [currentView, setCurrentView] = useState('main');

  const settingsSections = [
    { id: 'general',   icon: FiSettings,      title: 'General',           description: 'Website favicon and social sharing settings',        onClick: () => setCurrentView('general') },
    { id: 'domains',   icon: FiGlobe,         title: 'Domain Management', description: 'Subdomain and custom domain settings',               onClick: () => setCurrentView('domains') },
    { id: 'library',   icon: FiFolder,        title: 'Media Library',     description: 'View and manage all uploaded images',                onClick: () => onOpenLibrary() },
    { id: 'seo',       icon: FiSearch,        title: 'SEO Settings',      description: 'Configure meta tags and search engine optimization', onClick: () => setCurrentView('seo') },
    { id: 'widgets',   icon: FiMessageCircle, title: 'Widgets',           description: 'Floating WhatsApp button and other tools',           onClick: () => setCurrentView('widgets') },
    { id: 'analytics', icon: FiImage,         title: 'Analytics',         description: 'Track visitor interactions and conversions',         comingSoon: true },
    { id: 'security',  icon: FiLock,          title: 'Security',          description: 'Password protection and access controls',            comingSoon: true },
  ];

  const isDarkView = ['general', 'seo', 'domains', 'widgets'].includes(currentView);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className={`rounded-[2rem] shadow-2xl w-full overflow-hidden flex flex-col transition-all duration-300 ${
          isDarkView ? 'bg-[#111318] max-w-2xl h-[85vh]' : 'bg-white max-w-lg'
        }`}
      >
        <AnimatePresence mode="wait">
          {currentView === 'main' && (
            <motion.div key="main" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="flex flex-col h-full">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/80">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-gray-900 rounded-2xl">
                    <SafeIcon icon={FiSettings} className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-black text-gray-900 text-xl uppercase tracking-tight">Website Settings</h3>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Configure your website</p>
                  </div>
                </div>
                <button onClick={onClose} className="bg-white p-2.5 rounded-xl shadow-sm hover:shadow-md transition-all text-gray-400 hover:text-gray-600 border border-gray-100">
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-3 flex-1 overflow-y-auto">
                {settingsSections.map(section => (
                  <motion.button
                    key={section.id}
                    onClick={section.onClick}
                    disabled={section.comingSoon}
                    whileHover={!section.comingSoon ? { scale: 1.02 } : {}}
                    whileTap={!section.comingSoon ? { scale: 0.98 } : {}}
                    className={`w-full flex items-center space-x-4 p-4 rounded-2xl transition-all ${section.comingSoon ? 'bg-gray-50 cursor-not-allowed opacity-60' : 'bg-white border border-gray-100 hover:border-teal-200 hover:shadow-lg hover:shadow-teal-50 cursor-pointer'}`}
                  >
                    <div className={`p-3 rounded-xl ${section.comingSoon ? 'bg-gray-200' : section.id === 'widgets' ? 'bg-green-50' : 'bg-teal-50'}`}>
                      <SafeIcon icon={section.icon} className={`w-5 h-5 ${section.comingSoon ? 'text-gray-400' : section.id === 'widgets' ? 'text-green-600' : 'text-teal-600'}`} />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-gray-800">{section.title}</span>
                        {section.comingSoon && <span className="text-[9px] font-black bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full uppercase tracking-wider">Soon</span>}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">{section.description}</p>
                    </div>
                    {!section.comingSoon && <SafeIcon icon={FiChevronRight} className="w-5 h-5 text-gray-300" />}
                  </motion.button>
                ))}
              </div>
              <div className="p-6 bg-gray-50 border-t border-gray-100">
                <p className="text-[10px] text-gray-400 text-center font-medium">Need help? Check our documentation for more details.</p>
              </div>
            </motion.div>
          )}
          {currentView === 'domains' && (
            <motion.div key="domains" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="flex flex-col h-full">
              <DomainsView onBack={() => setCurrentView('main')} onClose={onClose} activeWebsiteId={activeWebsiteId} />
            </motion.div>
          )}
          {currentView === 'general' && (
            <motion.div key="general" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="flex flex-col h-full">
              <GeneralView onBack={() => setCurrentView('main')} onClose={onClose} activeWebsiteId={activeWebsiteId} />
            </motion.div>
          )}
          {currentView === 'seo' && (
            <motion.div key="seo" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="flex flex-col h-full">
              <SeoView onBack={() => setCurrentView('main')} onClose={onClose} activeWebsiteId={activeWebsiteId} />
            </motion.div>
          )}
          {currentView === 'widgets' && (
            <motion.div key="widgets" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} className="flex flex-col h-full">
              <WidgetsView onBack={() => setCurrentView('main')} onClose={onClose} activeWebsiteId={activeWebsiteId} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default SettingsPopup;